/**
 * ExtendAssignmentModal
 * ====================
 * Modal for assigning an existing program to additional clients or groups.
 * Used from AssignmentDetailPage to bulk-extend assignments.
 */

import { useState, useEffect } from 'react'
import { Modal, Button } from '../common'
import { useToast } from '../common/Toast'
import { assignmentService, clientService, groupService } from '../../services'
import { useAuthStore } from '../../stores'
import type {
  Client,
  Group,
  AssignmentInsert,
  SubscriptionTier,
} from '../../types/database.types'

type AssignmentMode = 'multiple' | 'group'

interface GroupWithMembers extends Group {
  member_count?: number
}

interface ExtendAssignmentModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (createdCount: number) => void
  programId: string
  programTitle: string
  programDurationWeeks?: number | null
  providerId: string
  providerTier: SubscriptionTier
  originalNotes?: string | null
}

export function ExtendAssignmentModal({
  isOpen,
  onClose,
  onSuccess,
  programId,
  programTitle,
  programDurationWeeks,
  providerId,
  providerTier,
  originalNotes,
}: ExtendAssignmentModalProps) {
  const { user } = useAuthStore()
  const toast = useToast()

  // Assignment mode (multiple clients or entire group)
  const [assignmentMode, setAssignmentMode] = useState<AssignmentMode>('multiple')

  // Data loading states
  const [clients, setClients] = useState<Client[]>([])
  const [groups, setGroups] = useState<GroupWithMembers[]>([])
  const [alreadyAssignedClientIds, setAlreadyAssignedClientIds] = useState<Set<string>>(new Set())
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingGroupMembers, setIsLoadingGroupMembers] = useState(false)

  // Selection state
  const [selectedClientIds, setSelectedClientIds] = useState<Set<string>>(new Set())
  const [selectedGroupId, setSelectedGroupId] = useState('')
  const [notes, setNotes] = useState(originalNotes || '')
  const [clientSearch, setClientSearch] = useState('')

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSendingEmails, setIsSendingEmails] = useState(false)
  const [error, setError] = useState('')
  const [groupFilteredCount, setGroupFilteredCount] = useState(0)

  // Load data when modal opens
  useEffect(() => {
    if (isOpen) {
      loadData()
      // Reset selection state
      setSelectedClientIds(new Set())
      setSelectedGroupId('')
      setClientSearch('')
      setError('')
      setGroupFilteredCount(0)
      setAssignmentMode('multiple')
      setNotes(originalNotes || '')
    }
  }, [isOpen, originalNotes])

  const loadData = async () => {
    setIsLoading(true)
    try {
      const [clientsData, groupsData, existingAssignments] = await Promise.all([
        clientService.getClients(providerId),
        groupService.getGroupsWithCounts(providerId),
        assignmentService.getClientsWithProgramAssignment(providerId, programId),
      ])

      setClients(clientsData)
      setGroups(groupsData)
      setAlreadyAssignedClientIds(new Set(existingAssignments))
    } catch (err) {
      setError('Failed to load data. Please try again.')
      console.error('Failed to load extend modal data:', err)
    } finally {
      setIsLoading(false)
    }
  }

  // Load group members when a group is selected
  useEffect(() => {
    const loadGroupMembers = async () => {
      if (selectedGroupId && assignmentMode === 'group') {
        setIsLoadingGroupMembers(true)
        setGroupFilteredCount(0)
        try {
          const memberIds = await groupService.getGroupMemberIds(selectedGroupId)
          // Filter out clients who already have this program assigned
          const availableMemberIds = memberIds.filter(
            id => !alreadyAssignedClientIds.has(id)
          )
          setSelectedClientIds(new Set(availableMemberIds))
          setGroupFilteredCount(memberIds.length - availableMemberIds.length)
        } catch (err) {
          console.error('Failed to load group members:', err)
          setError('Failed to load group members')
        } finally {
          setIsLoadingGroupMembers(false)
        }
      }
    }
    loadGroupMembers()
  }, [selectedGroupId, assignmentMode, alreadyAssignedClientIds])

  // Filter out already-assigned clients
  const availableClients = clients.filter(
    c => (c.status === 'active' || c.status === 'invited') &&
         !alreadyAssignedClientIds.has(c.id)
  )

  // Apply search filter
  const filteredClients = clientSearch
    ? availableClients.filter(
        c =>
          c.full_name.toLowerCase().includes(clientSearch.toLowerCase()) ||
          c.email.toLowerCase().includes(clientSearch.toLowerCase())
      )
    : availableClients

  const toggleClientSelection = (clientId: string) => {
    const newSelected = new Set(selectedClientIds)
    if (newSelected.has(clientId)) {
      newSelected.delete(clientId)
    } else {
      newSelected.add(clientId)
    }
    setSelectedClientIds(newSelected)
  }

  const selectAllClients = () => {
    setSelectedClientIds(new Set(filteredClients.map(c => c.id)))
  }

  const clearSelection = () => {
    setSelectedClientIds(new Set())
  }

  const handleSubmit = async () => {
    setError('')

    if (selectedClientIds.size === 0) {
      setError(
        assignmentMode === 'group'
          ? 'No available members in this group (all already have this program)'
          : 'Please select at least one client'
      )
      return
    }

    setIsSubmitting(true)
    try {
      // Calculate expiration based on program duration (with tier-based fallback)
      const expiresAt = assignmentService.getAccessExpirationForProgramDuration(
        programDurationWeeks ?? null,
        providerTier
      )

      const assignments: AssignmentInsert[] = Array.from(selectedClientIds).map(
        clientId => ({
          client_id: clientId,
          program_id: programId,
          provider_id: providerId,
          notes: notes.trim() || null,
          expires_at: expiresAt.toISOString(),
          status: 'active',
        })
      )

      // Create assignments and get full details (including client info)
      const result = await assignmentService.createBulkAssignmentsWithDetails(assignments)
      const createdAssignments = result.assignments
      const createdCount = createdAssignments.length

      if (createdCount === 0) {
        setError('Failed to assign program to any clients')
        return
      }

      // Auto-send email notifications
      if (createdAssignments.length > 0) {
        setIsSendingEmails(true)

        const emailResults = await assignmentService.sendBulkAssignmentEmails(
          createdAssignments,
          programTitle,
          user?.user_metadata?.full_name || 'Your Coach',
          0 // Exercise count - we don't have this in the modal context
        )

        const successCount = emailResults.filter((r) => r.success).length
        const failCount = emailResults.filter((r) => !r.success).length

        if (successCount > 0 && failCount === 0) {
          toast.success(`${successCount} email notification${successCount > 1 ? 's' : ''} sent`)
        } else if (successCount > 0 && failCount > 0) {
          toast.success(`${successCount} email${successCount > 1 ? 's' : ''} sent, ${failCount} failed`)
        } else if (failCount > 0) {
          toast.error(`Failed to send ${failCount} email notification${failCount > 1 ? 's' : ''}`)
        }

        setIsSendingEmails(false)
      }

      onSuccess(createdCount)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign program')
    } finally {
      setIsSubmitting(false)
      setIsSendingEmails(false)
    }
  }

  const getAccessDurationText = () => {
    // Use program duration if available
    if (programDurationWeeks && programDurationWeeks > 0) {
      return `${programDurationWeeks} weeks (program duration)`
    }
    // Fallback to tier-based
    switch (providerTier) {
      case 'basic':
        return '30 days (subscription default)'
      case 'platinum':
        return '1 year (subscription default)'
      default:
        return '90 days (subscription default)'
    }
  }

  // Count how many clients already have this program
  const alreadyAssignedCount = alreadyAssignedClientIds.size

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Assign Program to More Clients"
      size="lg"
    >
      {isLoading ? (
        <div className="py-8 text-center text-gray-500">
          <svg
            className="animate-spin h-8 w-8 mx-auto mb-4 text-blue-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Loading...
        </div>
      ) : availableClients.length === 0 && groups.length === 0 ? (
        <div className="py-8 text-center">
          <svg
            className="w-16 h-16 mx-auto mb-4 text-gray-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
          <p className="text-gray-500 mb-4">
            {alreadyAssignedCount > 0
              ? `All your clients (${alreadyAssignedCount}) already have this program assigned.`
              : 'No other clients available to assign.'}
          </p>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Program Display */}
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Program</p>
            <p className="font-medium text-gray-900">{programTitle}</p>
            {alreadyAssignedCount > 0 && (
              <p className="text-sm text-amber-600 mt-1">
                {alreadyAssignedCount} client{alreadyAssignedCount !== 1 ? 's' : ''} already assigned
              </p>
            )}
          </div>

          {/* Assignment Mode Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assign To
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="extendMode"
                  value="multiple"
                  checked={assignmentMode === 'multiple'}
                  onChange={() => {
                    setAssignmentMode('multiple')
                    setSelectedClientIds(new Set())
                    setSelectedGroupId('')
                    setGroupFilteredCount(0)
                  }}
                  className="h-4 w-4 text-blue-600"
                />
                <span className="text-gray-700">Multiple Clients</span>
              </label>
              {groups.length > 0 && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="extendMode"
                    value="group"
                    checked={assignmentMode === 'group'}
                    onChange={() => {
                      setAssignmentMode('group')
                      setSelectedClientIds(new Set())
                    }}
                    className="h-4 w-4 text-blue-600"
                  />
                  <span className="text-gray-700">Entire Group</span>
                </label>
              )}
            </div>
          </div>

          {/* Multiple Clients Selection */}
          {assignmentMode === 'multiple' && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Select Clients ({selectedClientIds.size} selected)
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={selectAllClients}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={clearSelection}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <input
                type="text"
                placeholder="Search clients..."
                value={clientSearch}
                onChange={e => setClientSearch(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-2"
              />
              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                {filteredClients.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    {clientSearch
                      ? 'No clients match your search'
                      : 'No available clients'}
                  </div>
                ) : (
                  <ul className="divide-y divide-gray-200">
                    {filteredClients.map(client => (
                      <li
                        key={client.id}
                        onClick={() => toggleClientSelection(client.id)}
                        className={`p-3 cursor-pointer hover:bg-gray-50 flex items-center gap-3 ${
                          selectedClientIds.has(client.id) ? 'bg-blue-50' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedClientIds.has(client.id)}
                          onChange={() => toggleClientSelection(client.id)}
                          className="h-4 w-4 text-blue-600 rounded border-gray-300"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {client.full_name}
                          </p>
                          <p className="text-sm text-gray-500 truncate">
                            {client.email}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* Group Selection */}
          {assignmentMode === 'group' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Group
              </label>
              <select
                value={selectedGroupId}
                onChange={e => setSelectedGroupId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a group</option>
                {groups.map(group => (
                  <option key={group.id} value={group.id}>
                    {group.name} ({group.member_count || 0} members)
                  </option>
                ))}
              </select>
              {isLoadingGroupMembers && (
                <p className="mt-1 text-sm text-blue-600">Loading group members...</p>
              )}
              {selectedGroupId && !isLoadingGroupMembers && (
                <div className="mt-1 text-sm">
                  {selectedClientIds.size > 0 ? (
                    <p className="text-green-600">
                      {selectedClientIds.size} member{selectedClientIds.size !== 1 ? 's' : ''} will be assigned
                    </p>
                  ) : (
                    <p className="text-amber-600">
                      All members in this group already have this program
                    </p>
                  )}
                  {groupFilteredCount > 0 && (
                    <p className="text-gray-500">
                      ({groupFilteredCount} member{groupFilteredCount !== 1 ? 's' : ''} already assigned, skipped)
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes for Client(s) (optional)
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Instructions or notes for your client(s)..."
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-sm text-gray-500">
              These notes will be visible to the client(s)
            </p>
          </div>

          {/* Access Duration Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900">Access Duration</h4>
            <p className="text-sm text-gray-600 mt-1">
              New assignments will be accessible for{' '}
              <span className="font-medium">{getAccessDurationText()}</span>
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose} disabled={isSubmitting || isSendingEmails}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              isLoading={isSubmitting || isSendingEmails}
              disabled={selectedClientIds.size === 0 || isSubmitting || isSendingEmails}
            >
              {isSendingEmails
                ? 'Sending emails...'
                : `Assign to ${selectedClientIds.size} Client${selectedClientIds.size !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}
